/**
 * Admin tenant views: list, detail, config panel, component card, import.
 *
 * Both languages live side by side so a review sees the pair in one diff.
 * `index.ts` merges this module into the catalogs under the `adminTenants` namespace.
 */
export const de = {
  list: {
    title: "Mandanten",
    subPrefix: "",
    subSuffix: "Mandanten",
    searchPlaceholder: "Mandanten suchen...",
    searchLabel: "Mandanten suchen",
    import: "Importieren",
    newTenant: "+ Neuer Mandant",
    importTitle: "OpenDeskTenant-CR aus YAML importieren",
    emptyFiltered: "Keine Mandanten gefunden.",
    emptyAll: "Noch keine Mandanten vorhanden. Erstellen Sie Ihren ersten Mandanten.",
    deleteTitle: "Mandant löschen",
    deleteMessagePrefix: "Der Mandant \"",
    deleteMessageSuffix: "\" und alle zugehörigen Ressourcen werden unwiderruflich gelöscht.",
    deleteConfirm: "Endgültig löschen",
    card: {
      appsLabel: "Apps",
      healthLabel: "Health",
      createdLabel: "Erstellt:",
      details: "Details",
      edit: "Bearbeiten",
      editAria: "bearbeiten",
      delete: "Löschen",
      deleteAria: "löschen",
    },
  },
  detail: {
    back: "Zurück",
    provisioning: "Provisionierung",
    stepLabel: "Schritt",
    appsReady: "Apps bereit",
    edition: "Edition",
    effectiveDomain: "Effektive Domain",
    created: "Erstellt",
    apps: "Anwendungen",
    dnsHeading: "DNS-Einträge",
    dnsHeadingFor: "DNS-Einträge für",
    dnsLoading: "Laden …",
    dnsUnavailable: "Keine DNS-Hinweise verfügbar. Der Mandant ist noch nicht bereit, oder es sind keine Mail-Dienste aktiviert.",
    dnsExplanation: "Diese Datensätze müssen in der autoritativen DNS-Zone gesetzt werden. Der Operator übernimmt die DNS-Konfiguration nicht selbst.",
    dnsFetchError: "DNS-Hinweise konnten nicht geladen werden.",
    dnsTable: {
      type: "Typ",
      name: "Name",
      value: "Wert",
      purpose: "Zweck",
      status: "Status",
      actions: "Aktionen",
      verified: "verifiziert",
      unverified: "unverifiziert",
      copy: "Kopieren",
      copied: "Kopiert",
      copyAriaPrefix: "Wert für",
      copyAriaSuffix: "kopieren",
    },
    conditions: "Bedingungen",
    actionsHeading: "Aktionen",
    backupStart: "Backup starten",
    backupStarted: "Backup gestartet.",
    backupFailedPrefix: "Backup fehlgeschlagen:",
    unknownError: "Unbekannter Fehler",
    reconcileForce: "Reconcile erzwingen",
    reconcileRunning: "Reconcile läuft…",
    reconcileTitle: "Erzwingt einen Reconcile-Lauf des Operators (gleichbedeutend mit dem Setzen der force-reconcile-Annotation).",
    reconcileRequested: "Reconcile angefordert. Der Operator startet einen vollständigen Reconcile-Lauf in den nächsten Sekunden.",
    reconcileFailedPrefix: "Reconcile fehlgeschlagen:",
    deleteTenant: "Mandant löschen",
    deleteFailedPrefix: "Löschen fehlgeschlagen:",
    noTenantId: "Keine Mandanten-ID angegeben.",
    notFound: "Mandant nicht gefunden.",
    deleteMessagePrefix: "Der Mandant \"",
    deleteMessageSuffix: "\" und alle zugehörigen Ressourcen werden unwiderruflich gelöscht.",
    compliance: {
      heading: "Compliance & Kontrollen",
      loading: "Lade Compliance-Daten…",
      noProfile: "Kein aktives Compliance-Profil für diesen Mandanten.",
      configureProfile: "Profil konfigurieren",
      noReport: "Kein ComplianceReport verfügbar.",
      profile: "Profil",
      score: "Score",
      violations: "Verstöße",
      openReport: "Bericht öffnen",
      openAudit: "Audit-Log öffnen",
    },
    maintenance: {
      heading: "Wartungsfenster",
      edit: "Bearbeiten",
      none: "Kein Wartungsfenster konfiguriert. Standardmäßig kann der Operator jederzeit reconciliieren.",
      summaryAt: "um",
      summaryUtc: "UTC für",
      summaryMinutes: "Minuten",
      weekday: "Wochentag",
      startTime: "Startzeit (UTC)",
      duration: "Dauer (Minuten, 30-480)",
      save: "Speichern",
      saving: "Speichern…",
      cancel: "Abbrechen",
      remove: "Fenster entfernen",
      saved: "Wartungsfenster gespeichert.",
      removed: "Wartungsfenster entfernt.",
      days: {
        Monday: "Montag",
        Tuesday: "Dienstag",
        Wednesday: "Mittwoch",
        Thursday: "Donnerstag",
        Friday: "Freitag",
        Saturday: "Samstag",
        Sunday: "Sonntag",
      },
    },
  },
  config: {
    back: "Zurück",
    titlePrefix: "Konfiguration:",
    tenantAdminSub: "Mandanten-Einstellungen",
    platformAdminSub: "Alle Konfigurationsfelder",
    banner: {
      live: "Live-Werte aktiv: effektive Konfiguration wird angezeigt",
      pending: "Operator-Defaults noch nicht berechnet. Die Werte kommen nach dem ersten Reconcile.",
    },
    feedback: {
      savedReconciled: "Gespeichert. Reconcile ausgelöst.",
      savedNoReconcile: "Gespeichert. Reconcile konnte nicht ausgelöst werden.",
      errorPrefix: "Fehler:",
      unknown: "Unbekannt",
    },
    sections: {
      allgemein: {
        title: "Allgemein",
        description: "Grundlegende Mandanten-Einstellungen",
        domain: "Domain",
        edition: "Edition",
        displayName: "Anzeigename",
        adminEmail: "Admin-E-Mail",
        additionalDomains: "Zusätzliche Domains",
        additionalDomainsPlaceholder: "Kommagetrennt",
        logoUrl: "Logo-URL",
        primaryColor: "Primärfarbe",
      },
      lifecycle: {
        title: "Lifecycle",
        description: "Mandanten-Lebenszyklus steuern",
        state: "Status",
        stateRunning: "Running",
        stateSuspended: "Suspended",
        stateHibernated: "Hibernated",
        reason: "Grund",
      },
      i18n: {
        title: "Internationalisierung",
        defaultLanguage: "Standardsprache",
        de: "Deutsch",
        en: "English",
        fr: "Français",
        es: "Español",
      },
      scaling: {
        title: "Skalierung",
        defaultSize: "Standardgröße",
        nightMode: "Nachtmodus",
        nightModeSchedule: "Zeitplan",
        vpaMode: "VPA-Modus",
      },
      groupware: {
        title: "Groupware & Mail",
        description: "E-Mail-Quoten, DAV-Unterstützung und MTA-Konfiguration",
        mailboxDefault: "Postfach-Standard",
        mailboxMax: "Postfach-Maximum",
        filestoreDefault: "Dateispeicher-Standard",
        filestoreMax: "Dateispeicher-Maximum",
        davSupport: "DAV-Unterstützung",
        mtaMode: "MTA-Modus",
        mtaGlobal: "Global",
        mtaMandant: "Mandant",
        mtaOverride: "Override",
        smtpRelay: "SMTP-Relay",
        dkim: "DKIM",
      },
      dateien: {
        title: "Dateien (Nextcloud)",
        quotaDefault: "Speicher-Standard",
        externalShare: "Externes Teilen",
        externalPassword: "Passwortpflicht",
        expiryActive: "Ablaufdatum aktiv",
        expiryDays: "Ablauf-Tage",
        expiryDaysUnit: "Tage",
        trashRetention: "Papierkorb-Aufbewahrung",
        versionsRetention: "Versionen-Aufbewahrung",
        auto: "auto",
      },
      chat: {
        title: "Chat & Video",
        description: "Matrix / Jitsi Einstellungen",
        allowDisplaynameChange: "Benutzer dürfen Anzeigenamen ändern",
        federation: "Federation",
        presence: "Präsenzanzeige",
        jitsiHistory: "Jitsi-Raumhistorie",
      },
      weboffice: {
        title: "Web-Office (Collabora)",
        defaultFormat: "Standardformat",
        macros: "Makros",
        macroSecurity: "Makro-Sicherheitsstufe",
        macroSecurityLow: "Niedrig (0)",
        macroSecurityMedium: "Mittel (1)",
      },
      network: {
        title: "Netzwerk & Sicherheit",
        description: "ALG, TLS, WAF, Rate Limits",
        isolation: "Isolation",
        isolationStrict: "Strict",
        isolationPermissive: "Permissive",
        stunMode: "STUN-Modus",
        stunServer: "STUN-Server",
        turnMode: "TURN-Modus",
        turnServer: "TURN-Server",
        turnCredential: "Credential-Secret",
        modeGlobal: "Global",
        modeCustom: "Custom",
        tlsMode: "TLS-Modus",
        tlsSan: "SAN",
        tlsWildcard: "Wildcard",
        tlsMin: "TLS-Minimum",
        hsts: "HSTS",
        wafMode: "WAF-Modus",
        wafDetection: "Detection",
        wafPrevention: "Prevention",
        botProtection: "Bot-Schutz",
        botOff: "Off",
        botChallenge: "Challenge",
        botBlock: "Block",
        rpsTenant: "Mandant RPS",
        rpsAuthLogin: "Auth-Login RPS",
        uploadLimit: "Upload-Limit",
        mailLimit: "Mail-Limit",
      },
      iam: {
        title: "IAM & Identität",
        provider: "Provider",
        providerKeycloak: "Keycloak (nativ)",
        providerNubus: "Nubus",
        federation: "Federation",
        federationStandalone: "Standalone",
        federationFederated: "Federated",
      },
      cluster: {
        title: "Cluster & Datenbank",
        distribution: "Distribution",
        distGeneric: "Generic",
        distRke2: "Rancher RKE2",
        distK3s: "K3s",
        distGardener: "Gardener",
        distStackit: "STACKIT SKE",
        distIonos: "IONOS DCD",
        storageBackend: "Storage-Backend",
        storageLonghorn: "Longhorn",
        storageRook: "Rook Ceph",
        dbBackend: "Datenbank-Backend",
        dbCnpg: "CloudNativePG",
        dbExternal: "External",
        dbTopology: "DB-Topologie",
        dbShared: "Shared",
        dbPerApp: "Per-App",
        dbSharedInstances: "Shared-Instanzen",
        dbSharedStorage: "Shared-Storage",
      },
      monitoring: {
        title: "Monitoring & Alerts",
        storageThreshold: "Speicher-Alarm",
        cpuThreshold: "CPU-Alarm",
        alertEmailRef: "Alert-E-Mail-Ref",
      },
      ai: {
        title: "KI-Engine",
        description: "Enterprise AI-Konfiguration",
        enterpriseOnly: "Enterprise Edition",
        backend: "LLM-Backend",
        backendIonos: "IONOS Sovereign",
        backendOllama: "Ollama",
        backendVllm: "vLLM",
        model: "Modell",
        endpoint: "Endpoint",
        guardrails: "Guardrails-Level",
        guardrailsLow: "Niedrig",
        guardrailsMedium: "Mittel",
        guardrailsHigh: "Hoch",
        dsgvo: "DSGVO-Modus",
        rateLimit: "Rate Limit",
      },
      platformDefaults: {
        title: "Platform-Defaults",
        description: "Effektive Werte des Operators, mit sichtbaren Mandanten-Overrides",
        mailboxDefault: "Postfach-Standard (GB)",
        filestoreDefault: "Dateispeicher-Standard (GB)",
        smtpRelay: "SMTP-Relay",
        iamProvider: "IAM-Provider",
        ingressClass: "Ingress-Klasse",
        certIssuer: "Cert-Issuer",
        defaultFormat: "Standard-Format (Collabora)",
        defaultSize: "Skalierung (DefaultSize)",
      },
    },
  },
  components: {
    heading: "Komponenten",
    loading: "Lade Komponenten …",
    empty: "Keine Komponenten registriert. Helm-Apps werden vom Provisioning-Schritt 14 angelegt.",
    fetchError: "Komponenten konnten nicht geladen werden.",
    table: {
      chart: "Chart",
      version: "Version",
      phase: "Phase",
      installed: "Installiert",
      open: "Öffnen ›",
      openAriaPrefix: "Komponente",
      openAriaSuffix: "öffnen",
    },
  },
  import: {
    title: "Mandant importieren",
    subtitle: "OpenDeskTenant-CR als YAML einfügen. Der Operator legt die Kubernetes-Ressource an und startet die Provisionierung wie beim Wizard.",
    back: "← Zurück zur Mandantenliste",
    yamlHeading: "YAML",
    yamlAriaLabel: "Tenant YAML",
    submit: "Importieren",
    submitting: "Importiere…",
    clear: "Leeren",
    loadExample: "Beispiel laden",
    previewHeading: "Vorschau",
    row: {
      name: "Name",
      namespace: "Namespace",
      namespaceDefault: "(default: opendesk-tenants)",
      domain: "Domain",
      edition: "Edition",
      release: "Release",
      apps: "Apps",
      maintenance: "Wartungsfenster",
      off: "(aus)",
      dash: "—",
    },
    errors: {
      empty: "Leeres YAML.",
      parsePrefix: "YAML-Parsefehler:",
      notObject: "YAML muss ein Objekt liefern (kein Array, kein Skalar).",
      unexpectedKindPrefix: "Unerwartete Kind:",
      unexpectedKindSuffix: "(erwartet OpenDeskTenant).",
      missingMetaName: "metadata.name fehlt.",
      missingSpec: "spec fehlt oder ist kein Objekt.",
      unrecognizedFormat: "Unerkanntes Format. Erwartet entweder eine OpenDeskTenant-CR (apiVersion + kind + metadata + spec) oder ein Objekt mit { name, namespace?, spec }.",
      conflict: "Mandant existiert bereits. Bitte einen anderen Namen wählen.",
      validationPrefix: "CRD-Validierung fehlgeschlagen:",
      forbidden: "Keine Berechtigung. platform-admin ist erforderlich.",
    },
  },
} as const;

export const en = {
  list: {
    title: "Tenants",
    subPrefix: "",
    subSuffix: "tenants",
    searchPlaceholder: "Search tenants...",
    searchLabel: "Search tenants",
    import: "Import",
    newTenant: "+ New tenant",
    importTitle: "Import an OpenDeskTenant CR from YAML",
    emptyFiltered: "No tenants found.",
    emptyAll: "No tenants yet. Create your first tenant.",
    deleteTitle: "Delete tenant",
    deleteMessagePrefix: "The tenant \"",
    deleteMessageSuffix: "\" and all its resources will be deleted permanently.",
    deleteConfirm: "Delete permanently",
    card: {
      appsLabel: "Apps",
      healthLabel: "Health",
      createdLabel: "Created:",
      details: "Details",
      edit: "Edit",
      editAria: "edit",
      delete: "Delete",
      deleteAria: "delete",
    },
  },
  detail: {
    back: "Back",
    provisioning: "Provisioning",
    stepLabel: "Step",
    appsReady: "Apps ready",
    edition: "Edition",
    effectiveDomain: "Effective domain",
    created: "Created",
    apps: "Applications",
    dnsHeading: "DNS records",
    dnsHeadingFor: "DNS records for",
    dnsLoading: "Loading …",
    dnsUnavailable: "No DNS hints available. The tenant is not ready yet or no mail services are enabled.",
    dnsExplanation: "These records must be set in the authoritative DNS zone. The operator does not manage DNS itself.",
    dnsFetchError: "Could not load DNS hints.",
    dnsTable: {
      type: "Type",
      name: "Name",
      value: "Value",
      purpose: "Purpose",
      status: "Status",
      actions: "Actions",
      verified: "verified",
      unverified: "unverified",
      copy: "Copy",
      copied: "Copied",
      copyAriaPrefix: "Copy value for",
      copyAriaSuffix: "",
    },
    conditions: "Conditions",
    actionsHeading: "Actions",
    backupStart: "Start backup",
    backupStarted: "Backup started.",
    backupFailedPrefix: "Backup failed:",
    unknownError: "Unknown error",
    reconcileForce: "Force reconcile",
    reconcileRunning: "Reconcile running…",
    reconcileTitle: "Forces an operator reconcile run (same effect as setting the force-reconcile annotation).",
    reconcileRequested: "Reconcile requested. The operator starts a full reconcile run within the next few seconds.",
    reconcileFailedPrefix: "Reconcile failed:",
    deleteTenant: "Delete tenant",
    deleteFailedPrefix: "Delete failed:",
    noTenantId: "No tenant ID given.",
    notFound: "Tenant not found.",
    deleteMessagePrefix: "The tenant \"",
    deleteMessageSuffix: "\" and all its resources will be deleted permanently.",
    compliance: {
      heading: "Compliance & controls",
      loading: "Loading compliance data…",
      noProfile: "No active compliance profile for this tenant.",
      configureProfile: "Configure profile",
      noReport: "No compliance report available.",
      profile: "Profile",
      score: "Score",
      violations: "Violations",
      openReport: "Open report",
      openAudit: "Open audit log",
    },
    maintenance: {
      heading: "Maintenance window",
      edit: "Edit",
      none: "No maintenance window configured. The operator can reconcile at any time by default.",
      summaryAt: "at",
      summaryUtc: "UTC for",
      summaryMinutes: "minutes",
      weekday: "Weekday",
      startTime: "Start time (UTC)",
      duration: "Duration (minutes, 30-480)",
      save: "Save",
      saving: "Saving…",
      cancel: "Cancel",
      remove: "Remove window",
      saved: "Maintenance window saved.",
      removed: "Maintenance window removed.",
      days: {
        Monday: "Monday",
        Tuesday: "Tuesday",
        Wednesday: "Wednesday",
        Thursday: "Thursday",
        Friday: "Friday",
        Saturday: "Saturday",
        Sunday: "Sunday",
      },
    },
  },
  config: {
    back: "Back",
    titlePrefix: "Configuration:",
    tenantAdminSub: "Tenant settings",
    platformAdminSub: "All configuration fields",
    banner: {
      live: "Live values active: showing the effective configuration",
      pending: "Operator defaults are not computed yet. The values arrive after the first reconcile.",
    },
    feedback: {
      savedReconciled: "Saved. Reconcile triggered.",
      savedNoReconcile: "Saved. Reconcile could not be triggered.",
      errorPrefix: "Error:",
      unknown: "Unknown",
    },
    sections: {
      allgemein: {
        title: "General",
        description: "Basic tenant settings",
        domain: "Domain",
        edition: "Edition",
        displayName: "Display name",
        adminEmail: "Admin email",
        additionalDomains: "Additional domains",
        additionalDomainsPlaceholder: "Comma-separated",
        logoUrl: "Logo URL",
        primaryColor: "Primary color",
      },
      lifecycle: {
        title: "Lifecycle",
        description: "Control the tenant lifecycle",
        state: "State",
        stateRunning: "Running",
        stateSuspended: "Suspended",
        stateHibernated: "Hibernated",
        reason: "Reason",
      },
      i18n: {
        title: "Internationalization",
        defaultLanguage: "Default language",
        de: "German",
        en: "English",
        fr: "French",
        es: "Spanish",
      },
      scaling: {
        title: "Scaling",
        defaultSize: "Default size",
        nightMode: "Night mode",
        nightModeSchedule: "Schedule",
        vpaMode: "VPA mode",
      },
      groupware: {
        title: "Groupware & mail",
        description: "Mail quotas, DAV support, and MTA configuration",
        mailboxDefault: "Mailbox default",
        mailboxMax: "Mailbox maximum",
        filestoreDefault: "File storage default",
        filestoreMax: "File storage maximum",
        davSupport: "DAV support",
        mtaMode: "MTA mode",
        mtaGlobal: "Global",
        mtaMandant: "Tenant",
        mtaOverride: "Override",
        smtpRelay: "SMTP relay",
        dkim: "DKIM",
      },
      dateien: {
        title: "Files (Nextcloud)",
        quotaDefault: "Storage default",
        externalShare: "External sharing",
        externalPassword: "Require password",
        expiryActive: "Expiry active by default",
        expiryDays: "Expiry days",
        expiryDaysUnit: "days",
        trashRetention: "Trash retention",
        versionsRetention: "Version retention",
        auto: "auto",
      },
      chat: {
        title: "Chat & video",
        description: "Matrix / Jitsi settings",
        allowDisplaynameChange: "Users may change their display name",
        federation: "Federation",
        presence: "Presence indicator",
        jitsiHistory: "Jitsi room history",
      },
      weboffice: {
        title: "Web office (Collabora)",
        defaultFormat: "Default format",
        macros: "Macros",
        macroSecurity: "Macro security level",
        macroSecurityLow: "Low (0)",
        macroSecurityMedium: "Medium (1)",
      },
      network: {
        title: "Network & security",
        description: "ALG, TLS, WAF, rate limits",
        isolation: "Isolation",
        isolationStrict: "Strict",
        isolationPermissive: "Permissive",
        stunMode: "STUN mode",
        stunServer: "STUN server",
        turnMode: "TURN mode",
        turnServer: "TURN server",
        turnCredential: "Credential secret",
        modeGlobal: "Global",
        modeCustom: "Custom",
        tlsMode: "TLS mode",
        tlsSan: "SAN",
        tlsWildcard: "Wildcard",
        tlsMin: "TLS minimum",
        hsts: "HSTS",
        wafMode: "WAF mode",
        wafDetection: "Detection",
        wafPrevention: "Prevention",
        botProtection: "Bot protection",
        botOff: "Off",
        botChallenge: "Challenge",
        botBlock: "Block",
        rpsTenant: "Tenant RPS",
        rpsAuthLogin: "Auth login RPS",
        uploadLimit: "Upload limit",
        mailLimit: "Mail limit",
      },
      iam: {
        title: "IAM & identity",
        provider: "Provider",
        providerKeycloak: "Keycloak (native)",
        providerNubus: "Nubus",
        federation: "Federation",
        federationStandalone: "Standalone",
        federationFederated: "Federated",
      },
      cluster: {
        title: "Cluster & database",
        distribution: "Distribution",
        distGeneric: "Generic",
        distRke2: "Rancher RKE2",
        distK3s: "K3s",
        distGardener: "Gardener",
        distStackit: "STACKIT SKE",
        distIonos: "IONOS DCD",
        storageBackend: "Storage backend",
        storageLonghorn: "Longhorn",
        storageRook: "Rook Ceph",
        dbBackend: "Database backend",
        dbCnpg: "CloudNativePG",
        dbExternal: "External",
        dbTopology: "DB topology",
        dbShared: "Shared",
        dbPerApp: "Per-app",
        dbSharedInstances: "Shared instances",
        dbSharedStorage: "Shared storage",
      },
      monitoring: {
        title: "Monitoring & alerts",
        storageThreshold: "Storage alert",
        cpuThreshold: "CPU alert",
        alertEmailRef: "Alert email ref",
      },
      ai: {
        title: "AI engine",
        description: "Enterprise AI configuration",
        enterpriseOnly: "Enterprise Edition",
        backend: "LLM backend",
        backendIonos: "IONOS Sovereign",
        backendOllama: "Ollama",
        backendVllm: "vLLM",
        model: "Model",
        endpoint: "Endpoint",
        guardrails: "Guardrails level",
        guardrailsLow: "Low",
        guardrailsMedium: "Medium",
        guardrailsHigh: "High",
        dsgvo: "GDPR mode",
        rateLimit: "Rate limit",
      },
      platformDefaults: {
        title: "Platform defaults",
        description: "Effective operator values, with tenant overrides shown",
        mailboxDefault: "Mailbox default (GB)",
        filestoreDefault: "File storage default (GB)",
        smtpRelay: "SMTP relay",
        iamProvider: "IAM provider",
        ingressClass: "Ingress class",
        certIssuer: "Cert issuer",
        defaultFormat: "Default format (Collabora)",
        defaultSize: "Scaling (default size)",
      },
    },
  },
  components: {
    heading: "Components",
    loading: "Loading components …",
    empty: "No components registered. Helm apps are created by provisioning step 14.",
    fetchError: "Could not load components.",
    table: {
      chart: "Chart",
      version: "Version",
      phase: "Phase",
      installed: "Installed",
      open: "Open ›",
      openAriaPrefix: "Open component",
      openAriaSuffix: "",
    },
  },
  import: {
    title: "Import tenant",
    subtitle: "Paste an OpenDeskTenant CR as YAML. The operator creates the Kubernetes resource and starts provisioning as the wizard would.",
    back: "← Back to tenant list",
    yamlHeading: "YAML",
    yamlAriaLabel: "Tenant YAML",
    submit: "Import",
    submitting: "Importing…",
    clear: "Clear",
    loadExample: "Load example",
    previewHeading: "Preview",
    row: {
      name: "Name",
      namespace: "Namespace",
      namespaceDefault: "(default: opendesk-tenants)",
      domain: "Domain",
      edition: "Edition",
      release: "Release",
      apps: "Apps",
      maintenance: "Maintenance window",
      off: "(off)",
      dash: "—",
    },
    errors: {
      empty: "Empty YAML.",
      parsePrefix: "YAML parse error:",
      notObject: "YAML must contain an object (not an array or scalar).",
      unexpectedKindPrefix: "Unexpected kind:",
      unexpectedKindSuffix: "(expected OpenDeskTenant).",
      missingMetaName: "metadata.name is missing.",
      missingSpec: "spec is missing or not an object.",
      unrecognizedFormat: "Unrecognized format. Expected either an OpenDeskTenant CR (apiVersion + kind + metadata + spec) or an object with { name, namespace?, spec }.",
      conflict: "Tenant already exists. Choose a different name.",
      validationPrefix: "CRD validation failed:",
      forbidden: "Not authorized. platform-admin is required.",
    },
  },
} as const;
